// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import Save from "@insite/mobius/Icons/Save";
import Trash2 from "@insite/mobius/Icons/Trash2";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import AddToList from "@sc/XDL/Icons/AddToList";
import Email from "@sc/XDL/Icons/Email";
import Plus from "@sc/XDL/Icons/Plus";
import Print from "@sc/XDL/Icons/Print";
import { color, createLinkIconStyles } from "@sc/XDL/Utilities";
import { css } from "styled-components";

interface MyAccountDetailsStyles {
    myAccountDetailsActions?: {
        saveOrderLink?: LinkPresentationProps;
        placeOrderLink?: LinkPresentationProps;
        acceptLink?: LinkPresentationProps;
        addAllToListClickable?: LinkPresentationProps;
        removeAllLink?: LinkPresentationProps;
        printLink?: LinkPresentationProps;
        emailLink?: ShareEntityButtonStyles;
    };
}
const iconStyles: IconPresentationProps = {
    size: 16,
    css: css`
        &,
        & > svg {
            height: 17px;
        }
    `,
};
const typographyStyles: TypographyPresentationProps = {
    size: 12,
    lineHeight: "22px",
    weight: "bold",
    color: "text.link",
};

const myAccountDetailsStyles: MyAccountDetailsStyles = {
    myAccountDetailsActions: {
        saveOrderLink: {
            ...createLinkIconStyles(Save, {}, typographyStyles, iconStyles),
        },
        placeOrderLink: {
            ...createLinkIconStyles(Save, {}, typographyStyles, iconStyles),
        },
        acceptLink: {
            ...createLinkIconStyles(Check, {}, typographyStyles, iconStyles),
        },
        addAllToListClickable: {
            ...createLinkIconStyles(AddToList, {}, typographyStyles, iconStyles),
        },
        removeAllLink: {
            ...createLinkIconStyles(Trash2, {}, typographyStyles, iconStyles),
        },
        printLink: {
            ...createLinkIconStyles(Print, {
                icon: {
                    iconProps: {
                        css: css`
                            &,
                            svg {
                                width: 12px;
                            }
                        `,
                    },
                },
            }),
        },
        emailLink: {
            button: {
                variant: "tertiary",
                color: "text.link",
                icon: {
                    src: Email,
                    position: "left",
                },
                typographyProps: {
                    ...typographyStyles,
                },
                css: css`
                    border: none;
                    padding: 0;
                    min-height: auto;
                    align-items: baseline;
                    ${IconWrapper}, svg {
                        width: 12px;
                    }
                `,
            },
        },
    },
};

export const linkLikeButtonCss: any = {
    icon: {
        position: "left",
    },
    typographyProps: {
        size: 12,
        lineHeight: "13px",
        weight: "bold",
        css: css`
            color: ${color("text.link")};
        `,
    },
    css: css`
        padding: 0;
        margin: 0;
        transition: none;
        border: none;
        min-height: auto;
        ${IconWrapper}, ${IconWrapper} svg {
            width: 13px;
            height: 13px;
            color: ${color("text.link")};
        }
        &&&:hover,
        &&&:focus {
            border: none;
            outline: none;
            text-decoration: underline;
        }
        &:hover:not([disabled]),
        &:focus:not([disabled]) {
            &,
            & ${IconWrapper}, ${TypographyStyle} {
                color: ${color("common.ui8")};
            }
        }
    `,
};

export const createListDetailButtonStyle = (iconSrc?: React.ComponentType | string) => {
    return {
        variant: "secondary",
        icon: {
            src: iconSrc,
            position: "right",
        },
        css: css`
            padding: 0 15px;
            margin-right: 10px;
            border-color: transparent;
            ${IconWrapper} {
                margin-bottom: 5px;
            }
        `,
    } as ButtonPresentationProps;
};

export default myAccountDetailsStyles;
